"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { useEffect, useState } from "react";
import {
  CheckSquare,
  FolderKanban,
  Brain,
  FileText,
  Calendar,
  Building2,
  ClipboardList,
  Menu,
  Plus,
  X,
  Lightbulb,
  LogOut,
  Shield,
  TrendingUp,
  Users,
  MessageSquare,
  Map,
  Cog,
  Package,
  Zap,
  BarChart3,
  Target,
  Palette,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { ThemeToggle } from "@/components/ThemeToggle";
import { NewItemModal } from "@/components/NewItemModal";
import { useAuthActions } from "@convex-dev/auth/react";
import { useQuery } from "convex/react";
import { api } from "../../convex/_generated/api";

type NavItem = {
  href: string;
  label: string;
  icon: typeof CheckSquare;
  comingSoon?: boolean;
};

type NavSection = {
  label: string;
  items: NavItem[];
  roleGated?: boolean;
};

const growthOsItems: NavItem[] = [
  { href: "/growth-os/marketing-assets", label: "Marketing Asset Inventory", icon: Package },
  { href: "/growth-os/value-ladder", label: "Value Ladder Canvas", icon: TrendingUp },
  { href: "/growth-os/ideal-client", label: "Ideal Client Profiler", icon: Users },
  { href: "/growth-os/core-message", label: "Core Message Canvas", icon: MessageSquare },
  { href: "/growth-os/customer-value-journey", label: "Customer Value Journey", icon: Map },
  { href: "/growth-os/value-engine", label: "Value Engine Planner", icon: Cog },
  { href: "/growth-os/growth-engine", label: "Growth Engine", icon: Zap },
  { href: "/growth-os/growth-scorecard", label: "Growth Scorecard", icon: BarChart3 },
  { href: "/growth-os/growth-ideas", label: "Growth Idea Sheet", icon: Lightbulb },
  { href: "/growth-os/big-3", label: "Big 3", icon: Target },
  { href: "/growth-os/brand-kit", label: "Brand Kit", icon: Palette, comingSoon: true },
];

const workItems: NavItem[] = [
  { href: "/projects", label: "Projects", icon: FolderKanban },
  { href: "/tasks", label: "Tasks", icon: CheckSquare },
  { href: "/ideas", label: "Ideas", icon: Lightbulb },
  { href: "/documents", label: "Documents", icon: FileText },
];

const agencyItems: NavItem[] = [
  { href: "/clients", label: "Clients", icon: Building2 },
  { href: "/assessments", label: "Assessments", icon: ClipboardList },
  { href: "/calendar", label: "Calendar", icon: Calendar },
  { href: "/office", label: "Office", icon: Building2 },
  { href: "/memory", label: "Memory", icon: Brain },
  { href: "/admin", label: "Admin", icon: Shield },
];

function UserFooter() {
  const { signOut } = useAuthActions();
  const currentUser = useQuery(api.ideas.currentUser);

  const displayName = currentUser?.name ?? currentUser?.email ?? "You";
  const initials = displayName
    .split(" ")
    .map((p: string) => p[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);

  return (
    <div className="flex items-center gap-2 border-t border-sidebar-border px-4 py-3">
      <div className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-primary/20 text-xs font-semibold text-primary">
        {initials}
      </div>
      <div className="min-w-0 flex-1">
        <div className="truncate text-xs font-medium leading-tight">{displayName}</div>
        {currentUser?.role && (
          <div className="text-[10px] leading-tight text-muted-foreground capitalize">
            {currentUser.role}
          </div>
        )}
      </div>
      <div className="flex items-center gap-1">
        <ThemeToggle />
        <button
          type="button"
          onClick={() => void signOut()}
          title="Sign out"
          className="inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
        >
          <LogOut className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}

function SidebarContent({
  onNavigate,
  onOpenNew,
}: {
  onNavigate?: () => void;
  onOpenNew: () => void;
}) {
  const pathname = usePathname();
  const currentUser = useQuery(api.ideas.currentUser);

  const role = currentUser?.role as string | undefined;
  const isAgencyUser = role === "admin" || role === "risdall";

  const sections: NavSection[] = [
    { label: "GROWTH OS", items: growthOsItems },
    { label: "WORK", items: workItems },
    { label: "AGENCY", items: agencyItems, roleGated: true },
  ];

  return (
    <>
      <div className="flex items-center gap-3 border-b border-sidebar-border px-5 py-5">
        <div
          className="w-8 h-8 rounded-md flex items-center justify-center text-white text-sm font-semibold flex-shrink-0 shadow-sm"
          style={{ backgroundImage: "linear-gradient(135deg, #7336d6 0%, #b266ff 100%)" }}
        >
          B
        </div>
        <div className="min-w-0">
          <div className="text-base font-semibold leading-tight tracking-tight md:text-sm">
            Backchannel
          </div>
          <div className="mt-0.5 text-sm leading-tight text-muted-foreground md:text-xs">
            Mission Control
          </div>
        </div>
      </div>

      <div className="px-3 pt-4">
        <button
          type="button"
          onClick={onOpenNew}
          className="flex w-full items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-indigo-500 to-violet-500 px-3 py-2 text-sm font-medium text-white shadow-sm transition-all hover:from-indigo-400 hover:to-violet-400 hover:shadow"
        >
          <Plus className="h-4 w-4" />
          New
        </button>
      </div>

      <nav className="flex-1 overflow-y-auto px-3 py-4">
        {sections.map((section) => {
          if (section.roleGated && !isAgencyUser) return null;

          return (
            <div key={section.label} className="mt-4 first:mt-0">
              <div className="mb-1.5 px-3 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground/70">
                {section.label}
              </div>
              <div className="space-y-0.5">
                {section.items.map((item) => {
                  const Icon = item.icon;
                  const isActive =
                    item.href === "/"
                      ? pathname === "/"
                      : pathname.startsWith(item.href);
                  return (
                    <Link
                      key={item.href}
                      href={item.href}
                      onClick={onNavigate}
                      className={cn(
                        "flex items-center gap-3 rounded-xl border px-3 py-2.5 text-[15px] transition-colors md:rounded-lg md:py-2 md:text-sm",
                        isActive
                          ? "border-primary/15 bg-primary/10 text-sidebar-foreground font-medium shadow-sm"
                          : "border-transparent text-sidebar-foreground/75 hover:border-sidebar-border hover:bg-sidebar-accent/80 hover:text-sidebar-foreground",
                        item.comingSoon && !isActive && "text-sidebar-foreground/50",
                      )}
                    >
                      <Icon
                        className={cn(
                          "w-4 h-4 flex-shrink-0",
                          isActive ? "text-primary" : "text-muted-foreground",
                          item.comingSoon && !isActive && "text-muted-foreground/50",
                        )}
                      />
                      {item.label}
                      {item.comingSoon && (
                        <span className="ml-auto text-[9px] font-medium uppercase tracking-wide bg-muted text-muted-foreground rounded px-1 py-0.5">
                          Soon
                        </span>
                      )}
                    </Link>
                  );
                })}
              </div>
            </div>
          );
        })}
      </nav>

      <UserFooter />
    </>
  );
}

export function Sidebar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [newOpen, setNewOpen] = useState(false);

  useEffect(() => {
    if (!mobileOpen) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, [mobileOpen]);

  return (
    <>
      <div className="fixed inset-x-0 top-0 z-50 flex items-center justify-between border-b border-sidebar-border bg-background/92 px-4 py-3.5 backdrop-blur-sm md:hidden">
        <Link href="/" className="flex min-w-0 items-center gap-3">
          <div
            className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md text-sm font-semibold text-white shadow-sm"
            style={{ backgroundImage: "linear-gradient(135deg, #7336d6 0%, #b266ff 100%)" }}
          >
            B
          </div>
          <div className="min-w-0">
            <div className="text-base font-semibold leading-tight tracking-tight text-foreground">
              Backchannel
            </div>
            <div className="text-sm leading-tight text-muted-foreground">
              Mission Control
            </div>
          </div>
        </Link>
        <div className="flex items-center gap-2">
          <ThemeToggle className="h-9 w-9" />
          <button
            type="button"
            aria-label={mobileOpen ? "Close navigation" : "Open navigation"}
            onClick={() => setMobileOpen((open) => !open)}
            className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-border/70 bg-background/80 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          >
            {mobileOpen ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
          </button>
        </div>
      </div>

      <aside className="fixed left-0 top-0 z-40 hidden h-full w-60 flex-col border-r border-sidebar-border bg-sidebar/96 text-sidebar-foreground shadow-[1px_0_0_rgba(0,0,0,0.02)] backdrop-blur-sm md:flex">
        <SidebarContent onOpenNew={() => setNewOpen(true)} />
      </aside>

      <div
        className={cn(
          "fixed inset-0 z-40 bg-black/40 backdrop-blur-[1px] transition-opacity md:hidden",
          mobileOpen ? "pointer-events-auto opacity-100" : "pointer-events-none opacity-0"
        )}
        onClick={() => setMobileOpen(false)}
      />

      <aside
        className={cn(
          "fixed inset-y-0 left-0 z-50 flex w-72 max-w-[86vw] flex-col border-r border-sidebar-border bg-sidebar text-sidebar-foreground shadow-xl transition-transform md:hidden",
          mobileOpen ? "translate-x-0" : "-translate-x-full"
        )}
      >
        <div className="flex items-center justify-end border-b border-sidebar-border px-4 py-3">
          <button
            type="button"
            aria-label="Close navigation"
            onClick={() => setMobileOpen(false)}
            className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-border/70 bg-background/80 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <SidebarContent
          onNavigate={() => setMobileOpen(false)}
          onOpenNew={() => {
            setMobileOpen(false);
            setNewOpen(true);
          }}
        />
      </aside>

      <NewItemModal open={newOpen} onClose={() => setNewOpen(false)} />
    </>
  );
}
